import type { QueuedRecording, ResearchStorageAdapter, UploadResult } from '../types';

export class HttpWorkerAdapter implements ResearchStorageAdapter {
  readonly id = 'cloudflare-worker' as const;

  constructor(
    private readonly baseUrl: string,
    private readonly token: string
  ) {}

  isEnabled(): boolean {
    return this.baseUrl.length > 0;
  }

  private headers(extra?: Record<string, string>): Headers {
    const headers = new Headers(extra);
    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`);
    }
    return headers;
  }

  async upload(item: QueuedRecording): Promise<UploadResult> {
    const url = `${this.baseUrl.replace(/\/$/, '')}/upload`;
    const contentType =
      item.encoding === 'gzip' ? 'application/gzip' : 'application/json';
    const response = await fetch(url, {
      method: 'POST',
      headers: this.headers({
        'Content-Type': contentType,
        'X-MoveAge-Recording-Id': item.recordingId,
        'X-MoveAge-Participant-Id': item.participantId,
        'X-MoveAge-Test-Id': item.testId,
        'X-MoveAge-Schema-Version': item.schemaVersion,
        'X-MoveAge-Consent-Version': item.consentVersion,
        'X-MoveAge-Encoding': item.encoding,
      }),
      body: item.bytes,
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`upload failed (${String(response.status)}): ${text.slice(0, 200)}`);
    }
    const json = (await response.json()) as { objectKey?: string };
    if (!json.objectKey) {
      throw new Error('upload response missing objectKey');
    }
    return { objectKey: json.objectKey };
  }

  async deleteParticipant(participantId: string): Promise<void> {
    const url = `${this.baseUrl.replace(/\/$/, '')}/delete`;
    const response = await fetch(url, {
      method: 'POST',
      headers: this.headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ participantId }),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`delete failed (${String(response.status)}): ${text.slice(0, 200)}`);
    }
  }
}
